import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { vegaCopyConversation } from '@gonfalon/dogfood-flags/dogfood-flags';

import { extractResultText, isPlanModeTool } from '../../agentInsightsUtils';
import type { CompletedComponent } from '../../agentInsightsUtils';
import { InferenceErrorMessage } from '../../internal/components/AgentInsights/components/messages/InferenceErrorMessage';
import { PlanModeMessage } from '../../internal/components/AgentInsights/components/messages/PlanModeMessage';
import { ResultMessage } from '../../internal/components/AgentInsights/components/messages/ResultMessage';
import { UserMessage } from '../../internal/components/AgentInsights/components/messages/UserMessage';
import type { ResourceOrQuery } from '../../resources';
import { trackVegaAiMessageCopied } from '../../vegaAnalytics';
import {
  classifyTool,
  commandGroupIcon,
  CommandGroupRow,
  ThoughtProcessSection,
  TOOL_CLASS_ICON,
  ToolCallRow,
  TranscriptRow,
} from '../conversation';
import type { RowSpace } from '../conversation/TranscriptRow';

import { isAlertPreviewData } from './alertPreviewData';
import { planCompletedComponents } from './completedComponentActivities';
import { ShimmerText } from './components/messages/ShimmerText';
import { getToolOutputString, isGraphPreviewData, tryParseToolData } from './graphPreviewData';
import { useToolResultRendererContext } from './ToolResultRendererContext';

const commandPreview = (toolInputs: unknown): string | undefined => {
  if (!toolInputs || typeof toolInputs !== 'object' || !('command' in toolInputs)) {
    return undefined;
  }
  return typeof toolInputs.command === 'string' && toolInputs.command.trim() ? toolInputs.command : undefined;
};

const shouldAutoExpand = (component: CompletedComponent): boolean => {
  if (!component.content) {
    return false;
  }
  const parsed = tryParseToolData(component.content);
  return isGraphPreviewData(parsed) || isAlertPreviewData(parsed);
};

const toolCallIsLoading = (component: CompletedComponent): boolean => {
  const hasCommandInput = commandPreview(component.toolInputs) != null;
  const output = getToolOutputString(component.content);
  if (hasCommandInput && !output.trim()) {
    return false;
  }
  return !component.content;
};

export const ConversationThread: FC<{
  components: CompletedComponent[];
  isAnalyzing: boolean;
  loadingText: string;
  latestEditableTextIndex: number;
  onSetAgentResponse?: (response: string) => void;
}> = ({ components, isAnalyzing, loadingText, latestEditableTextIndex, onSetAgentResponse }) => {
  const toolResultRenderer = useToolResultRendererContext();
  const copyEnabled = vegaCopyConversation();
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set());
  const slots = useMemo(
    () =>
      planCompletedComponents(
        components,
        (component) => !(component.type === 'tool' && component.rawToolName && isPlanModeTool(component.rawToolName)),
      ),
    [components],
  );

  const toggle = (id: string) => {
    setOpenIds((previous) => {
      const next = new Set(previous);
      if (!next.delete(id)) {
        next.add(id);
      }
      return next;
    });
  };

  const renderTool = (index: number) => {
    const component = components[index];
    if (!component) {
      return null;
    }
    const preview = commandPreview(component.toolInputs);
    const autoExpand = shouldAutoExpand(component);
    const loading = toolCallIsLoading(component);
    const output = getToolOutputString(component.content);
    const showOutput = Boolean(component.content) && !(preview && !output.trim());
    let input: string | undefined;
    if (component.toolInputs != null) {
      input = typeof component.toolInputs === 'string' ? component.toolInputs : JSON.stringify(component.toolInputs);
    }
    return (
      <ToolCallRow
        icon={TOOL_CLASS_ICON[classifyTool({ title: component.toolName ?? '', preview })]}
        label={component.toolName || 'Tool'}
        detail={preview}
        input={input}
        output={output.trim() ? output : undefined}
        renderOutput={showOutput ? () => toolResultRenderer(component.content, component.toolInputs) : undefined}
        isLoading={loading}
        defaultExpanded={autoExpand}
        clampOutput={!autoExpand}
        onCopy={() => trackVegaAiMessageCopied('vega')}
      />
    );
  };

  const renderComponent = (index: number, inThought: boolean): ReactNode => {
    const component = components[index];
    if (!component) {
      return null;
    }
    if (component.type === 'user') {
      return (
        <UserMessage
          message={typeof component.content === 'string' ? component.content : ''}
          context={component.context as ResourceOrQuery[] | undefined}
          timestamp={component.timestamp}
          redesign
        />
      );
    }
    if (component.type === 'text') {
      return (
        <ResultMessage
          message={{ text: component.content }}
          timestamp={component.timestamp}
          redesign
          showCaption={!inThought}
          onSetAgentResponse={onSetAgentResponse && index === latestEditableTextIndex ? onSetAgentResponse : undefined}
        />
      );
    }
    if (component.type === 'error') {
      return <InferenceErrorMessage errorCode={component.errorCode} errorMessage={component.errorMessage} />;
    }
    if (component.type === 'tool' && component.rawToolName && isPlanModeTool(component.rawToolName)) {
      return (
        <PlanModeMessage
          variant={component.rawToolName === 'EnterPlanMode' ? 'enter' : 'exit'}
          toolInputs={component.rawToolName === 'ExitPlanMode' ? component.toolInputs : undefined}
        />
      );
    }
    if (component.type === 'tool') {
      return renderTool(index);
    }
    return null;
  };

  interface ChatRow {
    key: string;
    depth?: 0 | 1 | 2 | 3;
    treeLast?: boolean;
    spaceBelow?: RowSpace;
    content: ReactNode;
  }

  const rows: ChatRow[] = [];
  const markEnd = (space: RowSpace) => {
    const last = rows[rows.length - 1];
    if (last) {
      last.spaceBelow = space;
    }
  };

  slots.forEach((slot, slotIndex) => {
    if (slot.kind === 'component') {
      rows.push({
        key: `component-${slot.index}`,
        content: renderComponent(slot.index, false),
      });
      markEnd('group');
      return;
    }

    const open = openIds.has(slot.id);
    const loading = isAnalyzing && slotIndex === slots.length - 1;
    rows.push({
      key: slot.id,
      content: (
        <ThoughtProcessSection
          thought={slot.thought}
          isLoading={loading}
          expanded={open}
          onToggle={() => {
            toggle(slot.id);
          }}
        >
          {null}
        </ThoughtProcessSection>
      ),
    });

    if (open) {
      slot.items.forEach((item, itemIndex) => {
        if (item.kind === 'component') {
          rows.push({
            key: `reasoning-${item.index}`,
            depth: 1,
            content: renderComponent(item.index, true),
          });
          if (slot.items[itemIndex + 1]?.kind !== 'commands') {
            markEnd('message');
          }
          return;
        }

        const commandsOpen = openIds.has(item.id);
        const before = slot.items[itemIndex - 1];
        const reasoning = before?.kind === 'component' ? components[before.index] : undefined;
        const reasoningText = reasoning?.type === 'text' ? extractResultText(reasoning.content).trim() : '';
        const indices = item.activities.flatMap((activity) => activity.indices);
        rows.push({
          key: item.id,
          depth: 1,
          content: (
            <CommandGroupRow
              label={item.label}
              icon={commandGroupIcon(item.toolClass)}
              errorCount={item.errorCount}
              expanded={commandsOpen}
              copyText={copyEnabled && reasoningText ? reasoningText : undefined}
              onCopy={reasoningText ? () => trackVegaAiMessageCopied('vega') : undefined}
              onToggle={() => {
                toggle(item.id);
              }}
            />
          ),
        });

        if (!commandsOpen) {
          markEnd('item');
          return;
        }

        indices.forEach((index, stepIndex) => {
          rows.push({
            key: `tool-${index}`,
            depth: 2,
            treeLast: stepIndex === indices.length - 1,
            content: renderTool(index),
          });
        });
        markEnd('item');
      });
    }

    markEnd('thought');
  });

  return (
    <>
      {rows.map((row) => (
        <TranscriptRow key={row.key} depth={row.depth} treeLast={row.treeLast} spaceBelow={row.spaceBelow}>
          {row.content}
        </TranscriptRow>
      ))}
      {isAnalyzing ? <ShimmerText text={loadingText} isLoading /> : null}
    </>
  );
};
